import React from "react";
import {
  CALL_CONTEXT_ACTIVITY_OPTIONS,
  CALL_CONTEXT_SCOPE_OPTIONS,
  CALL_CONTEXT_USAGE_OPTIONS,
  CALL_CONTEXT_MAX_AGE_OPTIONS,
  normalizeCallContext,
} from "./callContextConfig";
import "./CallContextSettings.css";

/**
 * Template opt-in for using the contact's logged calls / meetings / voicemails
 * as writing context. Rendered as one feature row inside the editor's
 * "AI Intelligence" section; `embedded` drops the title when the host
 * section already names the feature.
 */
const CallContextSettings = ({ value, onChange, embedded = false }) => {
  const cfg = normalizeCallContext(value);
  const set = (patch) => onChange({ ...cfg, ...patch });

  const toggleActivity = (id) => {
    const has = cfg.activities.includes(id);
    if (has && cfg.activities.length === 1) return;
    set({ activities: has ? cfg.activities.filter((a) => a !== id) : [...cfg.activities, id] });
  };

  return (
    <div className="wst-ai-feature">
      <div className="wst-ai-feature-row">
        {!embedded && <div className="wst-ai-feature-icon"><i className="fas fa-phone-volume" /></div>}
        <div className="wst-ai-feature-info">
          {embedded ? (
            <span className="wst-ai-feature-name">Use logged calls for this template</span>
          ) : (
            <>
              <span className="wst-ai-feature-name">Call Context <span className="wst-ai-credit-badge free">Included</span></span>
              <span className="wst-ai-feature-hint">The email builds on what was said in the contact's logged calls and meetings (Notes)</span>
            </>
          )}
        </div>
        <button
          type="button"
          className={`wst-hyper-toggle-btn ${cfg.enabled ? "active" : ""}`}
          onClick={() => set({ enabled: !cfg.enabled })}
          aria-pressed={cfg.enabled}
          aria-label="Use call context"
        >
          <span className="wst-hyper-toggle-knob" />
        </button>
      </div>

      {cfg.enabled && (
        <div className="ccs">
          <div className="ccs-field">
            <span className="ccs-label">Which notes</span>
            <div className="ccs-chips">
              {CALL_CONTEXT_SCOPE_OPTIONS.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  title={o.desc}
                  className={`ccs-chip ${cfg.scope === o.id ? "ccs-chip--active" : ""}`}
                  onClick={() => set({ scope: o.id })}
                >
                  {o.label}
                </button>
              ))}
            </div>
            <div className="ccs-chips">
              {CALL_CONTEXT_ACTIVITY_OPTIONS.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  className={`ccs-chip ccs-chip--soft ${cfg.activities.includes(o.id) ? "ccs-chip--on" : ""}`}
                  onClick={() => toggleActivity(o.id)}
                >
                  <i className={`fa ${o.icon}`} /> {o.label}
                </button>
              ))}
              <label className="ccs-age">
                <span>max.</span>
                <select value={cfg.maxAgeDays} onChange={(e) => set({ maxAgeDays: Number(e.target.value) })}>
                  {[...new Set([...CALL_CONTEXT_MAX_AGE_OPTIONS, cfg.maxAgeDays])].sort((a, b) => a - b).map((d) => (
                    <option key={d} value={d}>{d} days old</option>
                  ))}
                </select>
              </label>
            </div>
            <span className="ccs-hint">{CALL_CONTEXT_SCOPE_OPTIONS.find((o) => o.id === cfg.scope)?.desc}</span>
          </div>

          <div className="ccs-field">
            <span className="ccs-label">How the email should use them</span>
            <div className="ccs-usage">
              {CALL_CONTEXT_USAGE_OPTIONS.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  className={`ccs-usage-card ${cfg.usage === o.id ? "ccs-usage-card--active" : ""}`}
                  onClick={() => set({ usage: o.id })}
                >
                  <i className={`fa ${o.icon}`} />
                  <span className="ccs-usage-name">{o.label}</span>
                  <span className="ccs-usage-desc">{o.desc}</span>
                </button>
              ))}
            </div>
            <textarea
              className="ccs-input"
              rows={2}
              maxLength={600}
              placeholder="Optional extra instructions, e.g. Always name the number of locations they mentioned and propose a demo date."
              value={value?.instructions ?? cfg.instructions}
              onChange={(e) => onChange({ ...cfg, instructions: e.target.value })}
            />
          </div>

          <div className="ccs-field">
            <span className="ccs-label">No matching call note</span>
            <div className="ccs-segment">
              <button
                type="button"
                className={cfg.whenMissing === "generate" ? "active" : ""}
                onClick={() => set({ whenMissing: "generate" })}
              >
                <i className="fa fa-pen-nib" /> Write without call context
              </button>
              <button
                type="button"
                className={cfg.whenMissing === "hold" ? "active" : ""}
                onClick={() => set({ whenMissing: "hold" })}
              >
                <i className="fa fa-hand" /> Hold the email
              </button>
            </div>
            {cfg.whenMissing === "hold" && (
              <span className="ccs-hint">Agents and Auto Send skip the contact, flows stop it and park it in SafeGuard review. In Normal and Bulk mode you see a warning and decide yourself.</span>
            )}
          </div>

          <div className="ccs-tip">
            <i className="fa-solid fa-info-circle" />
            <span>
              Notes marked <strong>Don't use in AI emails</strong> are always skipped. If the latest call says
              <strong> Not interested</strong>, agents, flows and Auto Send never send. Want the call mentioned at a
              fixed spot? Put an instruction like <code>{"{Briefly refer to our call}"}</code> there in the template.
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default CallContextSettings;
